/* Copyright (c) 2022 Xperi. All Rights Reserved.  */

import { Channel } from '../../core/entities/Channel';
import { GuideError } from '../../core/error';
import type { TivoPayloadAction } from '../../core/tivo/tivo-redux-wrapper';
import { TChannelV2, TGridGuideRow } from '../../core/types';
import { DateTime } from '../../core/utils';
import { TGuideInitialState } from './guide.props';
import {
    GUIDE_SET_ERROR,
    GUIDE_SET_IS_CARET_VISIBLE,
    GUIDE_SET_IS_SPINNER_ACTIVE,
    LOAD_GRID_GUIDE_CHANNELS,
    LOAD_GRID_GUIDE_FUTURE_ROWS,
    LOAD_GRID_GUIDE_PAST_ROWS,
    LOAD_GRID_GUIDE_ROWS,
    ON_LEFT_SET_PREVIOUS_PAGE,
    ON_RIGHT_SET_NEXT_PAGE,
    REFRESH_GRID_GUIDE,
    RESET_GRID_GUIDE_CHANNELS,
    RESET_GRID_GUIDE_ROWS,
    SET_CHANNELS_COUNT,
    SET_GRID_GUIDE_CACHE,
    SET_GRID_GUIDE_CHANNELS_CACHE,
    SET_GRID_GUIDE_OFFER_PREVIEW_DATA,
    SET_GRID_GUIDE_OFFSET,
    SET_GRID_GUIDE_ROWS,
    SET_GRID_GUIDE_ROWS_CACHE,
    SET_GRID_GUIDE_START_TIME,
    SET_GRID_GUIDE_STATION_IDS,
    SET_GRID_GUIDE_TIMESTAMP_WINDOW,
    SET_GUIDE_ROWS_SERVICE_FAIL,
    SET_IS_CHANNELS_SERVICE_FAIL,
    SET_IS_PAST_GUIDE_MODE,
} from './guide.types';

export const setGuideStartTimeAction = (
    startTime: DateTime,
    previousTimestamp: DateTime,
    currentTimestamp: DateTime,
    nextTimestamp: DateTime,
): TivoPayloadAction<Partial<TGuideInitialState>> => ({
    type: SET_GRID_GUIDE_START_TIME,
    payload: {
        startTime,
        previousTimestamp,
        currentTimestamp,
        nextTimestamp,
    },
});

export const setGridGuideOffsetAction = (
    offset: number,
): TivoPayloadAction<Partial<TGuideInitialState>> => ({
    type: SET_GRID_GUIDE_OFFSET,
    payload: {
        offset,
    },
});

export const setGridGuideStationIdsAction = (
    stationIds: string[],
): TivoPayloadAction<Partial<TGuideInitialState>> => ({
    type: SET_GRID_GUIDE_STATION_IDS,
    payload: {
        stationIds,
    },
});

export const guideSetErrorAction = (
    error: GuideError | null,
): TivoPayloadAction<Partial<TGuideInitialState>> => ({
    type: GUIDE_SET_ERROR,
    payload: {
        error,
    },
});

export const setGridGuideTimestampWindowAction = (
    previousTimestamp: DateTime,
    currentTimestamp: DateTime,
    nextTimestamp: DateTime,
): TivoPayloadAction<Partial<TGuideInitialState>> => ({
    type: SET_GRID_GUIDE_TIMESTAMP_WINDOW,
    payload: {
        previousTimestamp,
        currentTimestamp,
        nextTimestamp,
    },
});

export const resetGridGuideRowsAction = (): TivoPayloadAction<Partial<TGuideInitialState>> => ({
    type: RESET_GRID_GUIDE_ROWS,
    payload: {
        pastGuideRows: [],
        guideRows: [],
        futureGuideRows: [],
    },
});

export const setGridGuideRowsAction = (
    pastGuideRows: TGridGuideRow[],
    guideRows: TGridGuideRow[],
    futureGuideRows: TGridGuideRow[],
): TivoPayloadAction<Partial<TGuideInitialState>> => ({
    type: SET_GRID_GUIDE_ROWS,
    payload: {
        pastGuideRows,
        guideRows,
        futureGuideRows,
    },
});

export const loadGridGuideChannelsAction = (
    channels: TChannelV2[] | Channel[],
    channelsCount: number,
    stationIds: string[],
    isChannelsServiceFail: boolean,
): TivoPayloadAction<Partial<TGuideInitialState>> => ({
    type: LOAD_GRID_GUIDE_CHANNELS,
    payload: {
        channels,
        channelsCount,
        stationIds,
        isChannelsServiceFail,
    },
});

export const loadGridGuideRowsAction = (
    guideRows: TGridGuideRow[],
): TivoPayloadAction<Partial<TGuideInitialState>> => ({
    type: LOAD_GRID_GUIDE_ROWS,
    payload: {
        guideRows,
        isGuideRowsServiceFail: false,
    },
});

export const loadGridGuidePastRowsAction = (
    pastGuideRows: TGridGuideRow[],
): TivoPayloadAction<Partial<TGuideInitialState>> => ({
    type: LOAD_GRID_GUIDE_PAST_ROWS,
    payload: {
        pastGuideRows,
    },
});

export const loadGridGuideFutureRowsAction = (
    futureGuideRows: TGridGuideRow[],
): TivoPayloadAction<Partial<TGuideInitialState>> => ({
    type: LOAD_GRID_GUIDE_FUTURE_ROWS,
    payload: {
        futureGuideRows,
    },
});

export const resetGridGuideChannelsAction = (): TivoPayloadAction<Partial<TGuideInitialState>> => ({
    type: RESET_GRID_GUIDE_CHANNELS,
    payload: {
        channels: [],
    },
});

export const setIsPastGuideModeAction = (
    isPastGuideMode: boolean,
): TivoPayloadAction<Partial<TGuideInitialState>> => ({
    type: SET_IS_PAST_GUIDE_MODE,
    payload: {
        isPastGuideMode,
    },
});

export const onLeftSetPreviousPageAction = (
    previousTimestamp: DateTime,
    currentTimestamp: DateTime,
    nextTimestamp: DateTime,
    guideRows: TGridGuideRow[],
    pastGuideRows: TGridGuideRow[],
): TivoPayloadAction<Partial<TGuideInitialState>> => ({
    type: ON_LEFT_SET_PREVIOUS_PAGE,
    payload: {
        previousTimestamp,
        currentTimestamp,
        nextTimestamp,
        futureGuideRows: guideRows,
        guideRows: pastGuideRows,
    },
});

export const onRightSetNextPageAction = (
    previousTimestamp: DateTime,
    currentTimestamp: DateTime,
    nextTimestamp: DateTime,
    guideRows: TGridGuideRow[],
    futureGuideRows: TGridGuideRow[],
): TivoPayloadAction<Partial<TGuideInitialState>> => ({
    type: ON_RIGHT_SET_NEXT_PAGE,
    payload: {
        previousTimestamp,
        currentTimestamp,
        nextTimestamp,
        pastGuideRows: guideRows,
        guideRows: futureGuideRows,
    },
});

export const setChannelsCountAction = (
    channelsCount: number,
): TivoPayloadAction<Partial<TGuideInitialState>> => ({
    type: SET_CHANNELS_COUNT,
    payload: {
        channelsCount,
    },
});

export const setIsChannelsServiceFailAction = (
    isChannelsServiceFail: boolean,
): TivoPayloadAction<Partial<TGuideInitialState>> => ({
    type: SET_IS_CHANNELS_SERVICE_FAIL,
    payload: {
        isChannelsServiceFail,
    },
});

export const setIsGuideRowsServiceFailAction = (
    isGuideRowsServiceFail: boolean,
    dummyGuideRows: TGridGuideRow[],
    previousTimestamp: DateTime,
    currentTimestamp: DateTime,
    nextTimestamp: DateTime,
): TivoPayloadAction<Partial<TGuideInitialState>> => ({
    type: SET_GUIDE_ROWS_SERVICE_FAIL,
    payload: {
        isGuideRowsServiceFail,
        previousTimestamp,
        currentTimestamp,
        nextTimestamp,
        guideRows: dummyGuideRows,
        pastGuideRows: dummyGuideRows,
        futureGuideRows: dummyGuideRows,
    },
});

export const refreshGridGuideAction = (
    startTime: DateTime,
    currentTimestamp: DateTime,
    nextTimestamp: DateTime,
    previousTimestamp: DateTime,
): TivoPayloadAction<Partial<TGuideInitialState>> => ({
    type: REFRESH_GRID_GUIDE,
    payload: {
        startTime,
        currentTimestamp,
        nextTimestamp,
        previousTimestamp,
        isPastGuideMode: false,
        guideRows: [],
        pastGuideRows: [],
        futureGuideRows: [],
    },
});

export const setGridGuideRowsCacheAction = (
    guideRowsCache: Array<TGridGuideRow[][]>,
): TivoPayloadAction<Partial<TGuideInitialState>> => ({
    type: SET_GRID_GUIDE_ROWS_CACHE,
    payload: {
        guideRowsCache,
    },
});

export const setGridGuideChannelsCacheAction = (
    channelsCache: Array<TChannelV2[] | Channel[]>,
): TivoPayloadAction<Partial<TGuideInitialState>> => ({
    type: SET_GRID_GUIDE_CHANNELS_CACHE,
    payload: {
        channelsCache,
    },
});

export const setGridGuideCacheAction = (
    channelsCache: Array<TChannelV2[] | Channel[]>,
    guideRowsCache: Array<TGridGuideRow[][]>,
): TivoPayloadAction<Partial<TGuideInitialState>> => ({
    type: SET_GRID_GUIDE_CACHE,
    payload: {
        channelsCache,
        guideRowsCache,
    },
});

export const setGridGuideOfferPreviewDataAction = (
    offerPreview: object,
    offerId: string,
): TivoPayloadAction<Partial<TGuideInitialState>> => ({
    type: SET_GRID_GUIDE_OFFER_PREVIEW_DATA,
    payload: {
        offerPreview,
        offerId,
    },
});

export const guideSetIsCaretVisibleAction = (
    isCaretVisible: boolean,
): TivoPayloadAction<Partial<TGuideInitialState>> => ({
    type: GUIDE_SET_IS_CARET_VISIBLE,
    payload: {
        isCaretVisible,
    },
});

export const guideSetIsSpinnerActiveAction = (
    isSpinnerActive: boolean,
): TivoPayloadAction<Partial<TGuideInitialState>> => ({
    type: GUIDE_SET_IS_SPINNER_ACTIVE,
    payload: {
        isSpinnerActive,
    },
});
